import { useLocale } from '@ecomm/data-hooks'
import { type LeadGenResponseProps, leadGenCapture } from '@ecomm/data-leads'
import { logError } from '@ecomm/error-handling'
import { useOptimizelyTrack } from '@ecomm/optimizely-hooks'
import {
  COOKIE_LEAD_DATA,
  getLeadData,
  getValueFromLeadData,
  updateLeadData,
  useCookieChange
} from '@ecomm/shared-cookies'
import { useIdentify } from '@ecomm/shared-ninetailed'
import { useBusiness } from '@ecomm/smb-components/BusinessProvider/BusinessProvider'
import { ReferrerUrlContext } from '@ecomm/tracking/TrackingProvider/useReferrerUrl'
import {
  brazeTrackPromoView,
  handleBrazeTrackingEvent,
  setBrazeAttributeGoogleClientId
} from '@ecomm/tracking/braze'
import { trackLeadCapture } from '@ecomm/tracking/trackLeadCapture'
import { useGoogleAnalytics } from '@ecomm/tracking/useGoogleAnalytics'
import { useLeadGenCaptureV2 } from '@ecomm/tracking/useLeadGenCaptureV2'
import { handleLeadCaptureV2Failure } from '@ecomm/tracking/useLeadGenCaptureV2/leadGenCaptureV2'
import type { LeadGenCaptureResponse } from '@simplisafe/ss-ecomm-data/simplisafe'
import { getDeviceType } from '@simplisafe/ss-ecomm-data/utils/windowScreenSize'
import * as E from 'fp-ts/lib/Either'
import * as TE from 'fp-ts/lib/TaskEither'
import { pipe } from 'fp-ts/lib/function'
import { useContext, useState } from 'react'
import { useTracking } from 'react-tracking'
import { useBannerInteractionState } from '../useBannerInteractionState'
import { useCurrentPromo } from '../useCurrentPromo'
import { useGetPromoLeadSource } from '../useGetPromoLeadSource'
import { useLeadCaptureEmailSubmitted } from '../useLeadCaptureEmailSubmitted'

export type LeadCaptureFormPropsOnSubmit = (
  _email: string,
  _onFailure: (_errorText: string) => void
) => void

export type LeadCaptureFormPropsOnPhoneNumberSubmit = (_phone: string) => void

export const useHandleFormSubmission = () => {
  const trackLeadCaptureFailure = useOptimizelyTrack({
    eventKey: 'website_error'
  })
  const { trackEvent } = useTracking({ appSection: 'promoSubmit' })
  const [clientId] = useGoogleAnalytics()
  const locale = useLocale()
  const { isBusiness } = useBusiness()

  const [defaultEmail, setDefaultEmail] = useState<string>(
    getLeadData()?.['email'] || ''
  )

  const [defaultPhoneNumber, setDefaultPhoneNumber] = useState<string>(
    getLeadData()?.['phone'] || ''
  )

  useCookieChange(COOKIE_LEAD_DATA, data => {
    // @ts-expect-error - TODO: ECP-12322 this type is unknown and the data needs to be parsed
    const email = JSON.parse(data)?.email || ''
    setDefaultEmail(email)

    // @ts-expect-error - TODO: ECP-12322 this type is unknown and the data needs to be parsed
    const phone = JSON.parse(data)?.phone || ''
    setDefaultPhoneNumber(phone)
  })

  const channel = useContext(ReferrerUrlContext).channel

  const { promoCode, promoCodeWithMonitoring } = useCurrentPromo()
  const source = useGetPromoLeadSource()

  const leadGenCaptureV2 = useLeadGenCaptureV2()

  const { onCloseBanner } = useBannerInteractionState()
  const { isSubmitted, setIsSubmitted } = useLeadCaptureEmailSubmitted()
  const identify = useIdentify()

  // Get device type for business flow indicators
  const deviceType = getDeviceType().toLowerCase()

  // Determine source and sourceDetails for business flows
  const getBusinessSourceDetails = () =>
    isBusiness ? `business_header_${deviceType}` : undefined

  const handleEmailSubmit: LeadCaptureFormPropsOnSubmit = (
    email,
    onFailure
  ) => {
    const handleLeadCaptureFailure = (e?: unknown) => {
      e && logError(e instanceof Error ? e : new Error(JSON.stringify(e)))
      trackLeadCaptureFailure()
      onFailure("We've encountered an error. Please try again later.")
    }

    const handleLeadCaptureSuccess = (value: LeadGenResponseProps) => {
      setIsSubmitted(true)
      onCloseBanner()
      brazeTrackPromoView()
      trackLeadCapture(value, trackEvent, channel)
        .finally(() => {
          const businessSourceDetails = getBusinessSourceDetails()

          pipe(
            leadGenCaptureV2({
              source: isBusiness ? 'business_flow' : source,
              sourceDetail: businessSourceDetails,
              siteMetadata: { sourceType: 'promo' },
              email
            }),
            TE.mapError(e => {
              handleLeadCaptureV2Failure(e)
              handleLeadCaptureFailure()
            })
          )()
        })
        .catch(logError)
      identify(value)

      // set google client id for UK as custom braze attribute ECP-12023
      locale === 'en-GB' &&
        clientId &&
        setBrazeAttributeGoogleClientId(clientId)

      console.info(
        `In promotions useHandleFormSubmission called trackLeadCapture with ${JSON.stringify(
          { leadId: value.leadId, externalId: value.externalId }
        )}`
      )
    }

    const businessSourceDetails = getBusinessSourceDetails()
    leadGenCapture({
      email,
      promoCode: promoCodeWithMonitoring || promoCode || 'NO_CODE',
      source: isBusiness ? 'business_flow' : source,
      ...(businessSourceDetails && { leadSource: businessSourceDetails })
    })()
      .then(E.match(handleLeadCaptureFailure, handleLeadCaptureSuccess))
      .catch(handleLeadCaptureFailure)
  }

  // For Phone Number Capture Experiment (ECP-10762)
  const handlePhoneNumberSubmit: LeadCaptureFormPropsOnPhoneNumberSubmit =
    async phone => {
      const leadDataCapture: LeadGenCaptureResponse = {
        externalId: getValueFromLeadData('externalId') || '',
        leadSource: source
      }
      updateLeadData({ phone: phone })
      await handleBrazeTrackingEvent(leadDataCapture, channel)
      brazeTrackPromoView()
      setIsSubmitted(true)
      onCloseBanner()
    }

  return {
    defaultEmail,
    defaultPhoneNumber,
    isFormSubmitted: isSubmitted,
    handleEmailSubmit,
    handlePhoneNumberSubmit
  }
}
